import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { TextAreaFormField } from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { useTranslation } from '@web/src/lib/intl/react'
import { CheckIcon, InfoIcon, MinusIcon, PlusIcon, UserPlusIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { EventAttendeeInfo, EventJoinRequestInfo, ProfileBasicInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { useAuth } from '../../../hooks/use-auth'

export const EVENT_JOIN_REQUEST = gql`
  mutation EventJoinRequest($eventIdOrSlug: String!, $input: EventJoinRequestInput!) {
    eventJoinRequest(eventIdOrSlug: $eventIdOrSlug, input: $input) {
      ...EventJoinRequestInfo
    }
  }
  ${EventJoinRequestInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const UPDATE_ATTENDANCE_STATUS = gql`
  mutation UpdateAttendanceStatus(
    $eventIdOrSlug: String!
    $status: EventAttendanceStatus!
    $input: EventJoinRequestInput
  ) {
    updateAttendanceStatus(eventIdOrSlug: $eventIdOrSlug, status: $status, input: $input) {
      ...EventAttendeeInfo
    }
  }
  ${EventAttendeeInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

export const EVENT_INVITATION = gql`
  mutation EventInvitationDialog($eventIdOrSlug: String!, $profileId: String!) {
    eventInvitation(eventIdOrSlug: $eventIdOrSlug, profileId: $profileId) {
      id
    }
  }
`

const EventAttendeeSignupDialog = ({
  event,
  open,
  onOpenChange,
  onRequestComplete,
  mode = 'request',
  invitationId = null,
  isFromInvitePage = false,
  status = 'GOING',
}) => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const [peopleCount, setPeopleCount] = useState(2)

  const formMethods = useForm({
    defaultValues: {
      notes: '',
    },
  })
  const { reset } = formMethods

  const [eventJoinRequest, { loading: requestLoading }] = useMutation(EVENT_JOIN_REQUEST, {
    onCompleted: () => {
      toast.success(t('Access request submitted'))
      closeDialog()
      if (onRequestComplete) {
        onRequestComplete()
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [updateAttendance, { loading: updateLoading }] = useMutation(UPDATE_ATTENDANCE_STATUS, {
    onCompleted: () => {
      toast.success(t('Attendance updated'))
      closeDialog()
      if (onRequestComplete) {
        onRequestComplete()
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [eventInvitation, { loading: forceAcceptLoading }] = useMutation(EVENT_INVITATION, {
    onCompleted: () => {
      toast.success(t('Invitation accepted'))
      closeDialog()
      if (onRequestComplete) {
        onRequestComplete()
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  useEffect(() => {
    if (open && peopleCount > 1) {
      const id = requestAnimationFrame(() => notesRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [open, peopleCount])

  const onSubmit = (data: { notes: string }) => {
    if (mode === 'request') {
      eventJoinRequest({
        variables: {
          eventIdOrSlug: event?.slug || event?.id,
          input: {
            notes: data.notes.trim(),
            peopleQty: peopleCount,
          },
        },
      })
    } else if (isFromInvitePage && invitationId) {
      eventInvitation({
        variables: {
          eventIdOrSlug: event?.slug || event?.id,
          profileId: currentUser.profile.id,
        },
      })
    } else {
      updateAttendance({
        variables: {
          eventIdOrSlug: event?.slug || event?.id,
          status: status || 'GOING',
          input: {
            notes: data.notes.trim(),
            peopleQty: peopleCount,
          },
        },
      })
    }
  }

  const closeDialog = () => {
    reset({
      notes: '',
    })
    setPeopleCount(2)
    if (onOpenChange) {
      onOpenChange({ open: false })
    }
  }

  const handleOnClose = () => {
    reset({
      notes: '',
    })
    setPeopleCount(2)
    closeDialog()
  }

  const incrementPeopleCount = () => {
    setPeopleCount(peopleCount + 1)
  }

  const decrementPeopleCount = () => {
    setPeopleCount(peopleCount - 1)
  }

  const isLoading = requestLoading || updateLoading || forceAcceptLoading

  return (
    <Dialog onOpenChange={(nextOpen) => onOpenChange?.({ open: nextOpen })} open={open}>
      <DialogContent initialFocus={false}>
        <Form formMethods={formMethods} onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === 'request' ? t('Request Event Access') : t('Confirm Attendance')}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-stretch gap-4 py-2">
            <div className="flex w-full flex-col items-start gap-2">
              <p className="font-medium">{t('Number of people attending (including yourself):')}</p>
              <div className="flex items-center gap-0">
                <Button
                  aria-label={t('Decrease count')}
                  disabled={peopleCount <= 1}
                  onClick={decrementPeopleCount}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <MinusIcon size={16} />
                </Button>
                <span className="min-w-16 bg-muted py-1 text-center font-bold text-lg">{peopleCount}</span>
                <Button
                  aria-label={t('Increase count')}
                  onClick={incrementPeopleCount}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <PlusIcon size={16} />
                </Button>
              </div>
            </div>

            {peopleCount > 1 && (
              <>
                <Alert>
                  <InfoIcon />
                  <AlertDescription>
                    {t('If your partner is attending with their own profile please do not include them in this count.')}
                  </AlertDescription>
                </Alert>

                <TextAreaFormField
                  fieldProps={{ className: 'pb-2' }}
                  helperText={t(
                    'Please provide the names of all additional people who will be attending with you. Only event organizers can see this information.'
                  )}
                  label={t('Names of attendees:')}
                  name="notes"
                  placeholder={t('Enter the names of who will be attending with you')}
                  ref={notesRef}
                  required
                  validation={{
                    validate: (v) => {
                      if (peopleCount <= 1) return true
                      return v.trim() ? true : t('Names of attendees are required')
                    },
                  }}
                />
              </>
            )}
          </div>

          <DialogFooter>
            <Button onClick={handleOnClose} type="button" variant="outline">
              {t('Cancel')}
            </Button>
            <Button className="gap-1.5" disabled={isLoading} type="submit">
              {isLoading ? <Spinner /> : mode === 'request' ? <UserPlusIcon /> : <CheckIcon />}
              {mode === 'request' ? t('Request Access') : t('Confirm Going')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default EventAttendeeSignupDialog
